import { CutlistOrder } from '@cutr/constants/cutlist';

import { useLeadDetails } from '@/api/account';
import { useDeliveryAddress } from '@/api/address';
import {
  getMaterial,
  groupSearchResultsByVariationGroupAndThickness,
} from '@/api/materials';
import {
  articleCodeToMaterialGroupMapper,
  useMaterialGroupState,
} from '@/api/materialsGroup';
import {
  computeCutlistNestingHash,
  persistedNestingFailures,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { usePricingStore } from '@/api/pricing';
import { partsSelector, useCutlistState } from '@/api/store';
import { materialGroupMapper, partMapper, resetStoreFn } from '@/hooks';
import { getCurrentFeatures } from '@/theme';

export const resetCutlistStores = () => resetStoreFn()();

export const hydrateCutlistCore = (cutlist: CutlistOrder) => {
  const parts = cutlist.materialGroups
    .flatMap((g) => g.parts)
    .sort((partA, partB) => partA.index - partB.index)
    .map(partMapper);

  useCutlistState.getState().init({
    ...cutlist,
    orderId: cutlist.id,
    title: cutlist.title || '',
    customerReference: cutlist.customerReference || '',
    internalPointOfContact: cutlist.internalPointOfContact || '',
    notes: cutlist.notes || '',
    requestedDeliveryDate: cutlist.requestedDeliveryDate?.split('T')[0] || '',
    discountAmount: cutlist.discountAmount || 0,
    discountPercentage: cutlist.discountPercentage || 0,
    markupAmount: cutlist.markupAmount || 0,
    markupPercentage: cutlist.markupPercentage || 0,
    authorAgent: cutlist.authorAgent || undefined,
    parts,
    thirdPartyId: cutlist.thirdPartyId || '',
  });
};

export const hydrateMaterialGroups = async (
  cutlist: CutlistOrder,
  articleCode?: string
) => {
  const groupStore = useMaterialGroupState.getState();

  if (cutlist.materialGroups.length) {
    const groups = cutlist.materialGroups.map((g) => materialGroupMapper(g));

    const activeGroup = groups[0]?.id;

    groupStore.init({ activeGroup, groups });
    return;
  }

  const material = getMaterial(articleCode);
  if (!material) {
    groupStore.addGroup();
    return;
  }

  const group = await articleCodeToMaterialGroupMapper(
    material.articleCode,
    getCurrentFeatures()
  );
  const sheetSizes = groupSearchResultsByVariationGroupAndThickness([
    material,
  ]).find(([m]) => m.thicknessUM === material.thicknessUM);

  if (sheetSizes) {
    group.sheetSizeSelection = 'automatic';
    group.automaticSheetSizeMaterials = sheetSizes.map((m) => m.articleCode);
    group.core1 = sheetSizes[0].articleCode;
  }

  groupStore.init({ activeGroup: group.id, groups: [group] });
};

export const hydrateStarterNestingAndPricing = (cutlist: CutlistOrder) => {
  if (!cutlist.materialGroups.length) return;

  const priceStore = usePricingStore.getState();
  priceStore.setAmountVat(cutlist.totalAmountInclVAT);
  priceStore.setAmountNoVat(cutlist.totalAmountExclVAT);
  priceStore.setPricing(cutlist.pricingLineItems);
  priceStore.toggleManualQuote(cutlist.manualQuote);
  priceStore.setError(null);

  const failedGroups = persistedNestingFailures(cutlist);
  const hasError = failedGroups.length > 0;
  if (!hasError && !cutlist.materialGroups.some((g) => g.nestingOutput)) return;

  const mixedNestingStore = useMixedNestingStore.getState();
  mixedNestingStore.setFailedGroups(hasError ? failedGroups : null);
  mixedNestingStore.setResults(cutlist);
  mixedNestingStore.setSnapshotHash(
    computeCutlistNestingHash(
      useMaterialGroupState.getState().groups,
      partsSelector(useCutlistState.getState())
    )
  );
  mixedNestingStore.setNestingState(hasError ? 'failed' : 'done');
};

export const hydrateDeliveryAddress = (cutlist: CutlistOrder) => {
  const deliveryAddress = cutlist.addresses[0];
  if (!deliveryAddress) return;

  useDeliveryAddress.getState().init({
    ...deliveryAddress,
    contactName: deliveryAddress.name,
  });
};

export const hydrateLead = (cutlist: CutlistOrder) => {
  const { userLeadDetail, userLead } = cutlist;

  useLeadDetails.getState().init({
    ...userLeadDetail,
    ...userLead,
    name: userLead?.name || userLeadDetail?.name,
    // @ts-ignore
    clientNumber: userLead?.clientNumber || userLeadDetail?.clientNumber,
    pricingCategory: userLead?.pricingCategory || undefined,
    customerType: userLead?.customerType || undefined,
    companyName: userLead?.companyName || userLeadDetail?.companyName,
    notClient: false,
  });
};

export const hydrateStarterCutlist = async (
  cutlist: CutlistOrder,
  articleCode?: string
) => {
  resetCutlistStores();
  hydrateCutlistCore(cutlist);
  await hydrateMaterialGroups(cutlist, articleCode);
  hydrateStarterNestingAndPricing(cutlist);
  hydrateDeliveryAddress(cutlist);
  hydrateLead(cutlist);
};
